const { withInterestCatalog } = require('./interest-catalog-routes');
const { ensureInterestCatalog } = require('services/auth-management/interest-catalog');
const AppLayout = require('containers/AppLayout');
const { context } = require('app-context');
const { USERS_SORT_TYPE, SEARCH_REFRESH_TIME_LIMIT, USER_ROLE_GROUPS_IDS } = require('utils/constants');
const { getUserId } = require('utils/analytics');
const { canViewGroup } = require('utils/groups');
const FallbackSpinner = require('./helpers/fallback-spinner');
const DelayedFallbackSpinner = require('./helpers/delayed-fallback-spinner');
const loadableRoute = require('./helpers/loadable-route');
const { auditRoutes } = require('./route-audit');
const { withRoutePermissions } = require('./route-permissions');
const {
    canBypassProfileRoleInteractions,
    dependencyPropTypes: profileDependencyPropTypes
} = require('./profile/profile-dependencies');
const { dependencyPropTypes: dashboardDependencyPropTypes } = require('./admin/dashboard-dependencies');
const TeamConsoleDependencies = require('./admin/team-console-dependencies');
const { dependencyPropTypes: teamConsoleDependencyPropTypes } = TeamConsoleDependencies;
const CreateCommunicationService = require('services/communication');
const RoutingTable = require('services/auth-management/routing-table');
const Continuation = require('services/auth-management/continuation');
const { getAppAuthenticationRoute } = require('services/auth-management/open-app-destination');
const AuthRoutes = require('routes/auth/config');

const WelcomePage = loadableRoute(() => import(/* webpackChunkName: "route-app-welcome" */ './welcome/containers/WelcomePage'));
const GamificationAdminPage = loadableRoute(() => import(/* webpackChunkName: "route-app-gamification-admin" */ './admin/components/GamificationAdminPage'));
const InviteFriendsPage = loadableRoute(() => import(/* webpackChunkName: "route-app-invite-friends" */ './referrals/InviteFriendsPage'));
const VerifyPhonePage = loadableRoute(() => import(/* webpackChunkName: "route-app-verify-phone" */ '../login/components/VerifyMfa'));
const NotFound = loadableRoute(() => import(/* webpackChunkName: "route-app-not-found" */ './components/NotFound'));
const ProfileDetailPage = loadableRoute(() => import(/* webpackChunkName: "route-app-profile-detail" */ './profile/containers/DetailPage'));
const ProfileEditPage = loadableRoute(() => import(/* webpackChunkName: "route-app-profile-edit" */ './profile/containers/EditPage'));
const ChangePasswordPage = loadableRoute(() => import(/* webpackChunkName: "route-app-profile-password" */ './profile/components/ChangePasswordPage'));
const InterestsEditPage = loadableRoute(() => import(/* webpackChunkName: "route-app-profile-interests" */ './profile/containers/EditInterests'));
const NerpyPage = loadableRoute(() => import(/* webpackChunkName: "route-app-nerpy-preview" */ './assistant/NerpyPage'));
const DirectMessagesPage = loadableRoute(() => import(/* webpackChunkName: "route-app-messaging-list" */ './connections/containers/DirectMessagesPage'));
const BugReportPage = loadableRoute(() => import(/* webpackChunkName: "route-app-bug-report" */ './bugReport/containers/BugReportPage'));
const UserPreferencesPage = loadableRoute(() => import(/* webpackChunkName: "route-app-preferences" */ './userPreferences/containers/UserPreferencesPage'));
const DirectMessageDetailPage = loadableRoute(() => import(/* webpackChunkName: "route-app-messaging-detail" */ './containers/Chat/DirectMessageDetailPage'));
const MyGroupsPage = loadableRoute(() => import(/* webpackChunkName: "route-app-classes-list" */ './classes/containers/MyGroupsPage'));
const ClassDetailPage = loadableRoute(() => import(/* webpackChunkName: "route-app-classes-detail" */ './classes/containers/ClassDetailPage'));
const ClassChatFrame = loadableRoute(() => import(/* webpackChunkName: "route-app-class-chat" */ './classes/containers/ClassChatFrame'));
const AnnouncementClassChatFrame = loadableRoute(() => import(/* webpackChunkName: "route-app-announcement-chat" */ './classes/containers/AnnouncementClassChatFrame'));
const DiscoverPage = loadableRoute(() => import(/* webpackChunkName: "route-app-discover" */ './discover/containers/DiscoverPage'));
const EditGroupPage = loadableRoute(() => import(/* webpackChunkName: "route-app-classes-edit" */ './classes/containers/EditGroupPage'));
const AdminContainer = loadableRoute(() => import(/* webpackChunkName: "route-app-admin" */ './admin/components/AdminContainer'));
const BatchNotify = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-batch-notify" */ './admin/containers/BatchNotify'));
const WelcomePageManagement = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-welcome-page" */ './admin/containers/WelcomePageManagement'));
const PreapprovedEmailUpload = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-preapprove" */ './admin/containers/PreapprovedEmailUpload'));
const AdminDashboard = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-dashboard" */ './admin/containers/AdminDashboard'));
const TeamConsole = loadableRoute(() => import(/* webpackChunkName: "route-app-team-console" */ './admin/components/TeamConsole'));
const SurveysContainer = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys" */ './surveys/components/SurveysContainer'));
const SurveysManagement = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-management" */ './surveys/containers/SurveysManagement'));
const SurveysCreatePage = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-create" */ './surveys/containers/SurveyCreate'));
const SurveysEditPage = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-edit" */ './surveys/containers/SurveyEdit'));
const SurveyReportPage = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-report" */ './surveys/containers/SurveyReport'));
const MySurveysPage = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-mine" */ './surveys/containers/MySurveys'));
const SurveySolvePage = loadableRoute(() => import(/* webpackChunkName: "route-app-surveys-solve" */ './surveys/containers/SurveySolvePage'));
const CommunityManagementPage = loadableRoute(() => import(/* webpackChunkName: "route-app-community-management" */ './communityManagement/CommunityManagement'));
const OffersPage = loadableRoute(() => import(/* webpackChunkName: "route-app-offers" */ './offers/containers/OffersPage'));
const OfferDetailPage = loadableRoute(() => import(/* webpackChunkName: "route-app-offer-detail" */ './offers/components/OfferDetailPage'));
const ResourcesPage = loadableRoute(() => import(/* webpackChunkName: "route-app-resources" */ './resources/containers/ResourcesPage'));
const ResourceDetailPage = loadableRoute(() => import(/* webpackChunkName: "route-app-resource-detail" */ './resources/components/ResourceDetailPage'));
const AdminOffersListPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-offers" */ './admin/offers/components/AdminOffersListPage'));
const AdminOfferFormPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-offer-form" */ './admin/offers/components/AdminOfferFormPage'));
const AdminOfferCreateMethodPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-offer-create-method" */ './admin/offers/components/AdminOfferCreateMethodPage'));
const AdminOfferUploadPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-offer-upload" */ './admin/offers/components/AdminOfferUploadPage'));
const AdminResourcesListPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-resources" */ './admin/resources/components/AdminResourcesListPage'));
const AdminResourceFormPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-resource-form" */ './admin/resources/components/AdminResourceFormPage'));
const AdminResourceCreateMethodPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-resource-create-method" */ './admin/resources/components/AdminResourceCreateMethodPage'));
const AdminResourceUploadPage = loadableRoute(() => import(/* webpackChunkName: "route-app-admin-resource-upload" */ './admin/resources/components/AdminResourceUploadPage'));

// Keep the app loader eager. It is the bootstrap page for refresh/login flows, so
// it must mount immediately instead of waiting on a lazy route chunk while the
// boot loader is already visible.
const AppLoader = require('components/AppLoader');

const communication = CreateCommunicationService.fromContext(context);
const actions = context.actions;
const selectors = context.selectors.all;
const handlers = context.handlers;
const redux = context.redux.hooks;
const api = context.api.nearpeer;

const internals = {};

internals.resolveAppRouteAuth = ({ handlers }) => {

    if (handlers.auth.resolveAuthStatus) {
        return handlers.auth.resolveAuthStatus();
    }

    return handlers.auth.resolveIsAuthenticated();
};

internals.resolveBootstrapSchoolId = ({ state, selectors, location }) => RoutingTable.getRequestedSchoolId(location)
    || selectors.getCurrentSchool?.(state)?.id
    || selectors.getLoginContext?.(state)?.schoolId;

internals.shouldBootstrapMainApp = ({ state, selectors, location }) => {

    const requestedSchoolId = RoutingTable.getRequestedSchoolId(location);
    const needsCommunitySwitch = !!requestedSchoolId
        && requestedSchoolId !== Number(selectors.getCurrentSchool?.(state)?.id);

    return selectors.getIsAuthenticated(state)
        && selectors.getVerifiedStatus(state)
        && (needsCommunitySwitch || (selectors.getMainAppLoading(state)
            && (!selectors.hasCurrentUser(state) || !selectors.hasCurrentSchool(state))));
};


internals.getRouteReplacement = ({ currentPath, destination }) => {

    if (!destination || destination === currentPath) {
        return undefined;
    }

    return { replace: destination };
};

// const teamConsoleEnabled = typeof __NEARPEER_TEAM_CONSOLE_ENABLED__ !== 'undefined' && __NEARPEER_TEAM_CONSOLE_ENABLED__;

module.exports = (store) => ({
    path: '/app',
    component: AppLayout,
    fallback: FallbackSpinner,
    wrapFallbackWithComponent: true,
    props: {
        fullWidthMobileRoutes: [
            '/app/messaging/',
            '/app/classes/chat/',
            '/app/classes/announcement/chat/'
        ]
    },
    onWillMount: async ({ match: { isExact }, location }) => {

        const currentPathname = location?.pathname || window.location.pathname || '';
        const currentPath = `${currentPathname}${location?.search || window.location.search || ''}`;
        const isAppRoot = currentPathname === '/app' || currentPathname === '/app/';
        const isAppLoaderRoute = currentPathname === AuthRoutes.app.loader;

        const appLoadRedirectPath = isExact && isAppRoot ? AuthRoutes.app.welcome : currentPath;
        const redirectContext = isAppLoaderRoute
            ? RoutingTable.getRedirectContext(location)
            : {
                redirect: RoutingTable.REDIRECTS.afterAuth,
                target: appLoadRedirectPath
            };

        try {
            await internals.resolveAppRouteAuth({
                handlers
            });

            const nextState = redux.getState();

            const isAuthenticated = selectors.getIsAuthenticated(nextState);
            const isVerified = selectors.getVerifiedStatus(nextState);

            if (isAppLoaderRoute) {
                const authDestination = !isAuthenticated && Continuation.peek().target
                    ? getAppAuthenticationRoute({ selectors, state: nextState })
                    : RoutingTable.resolveAppLoaderAuthDestination();

                if (authDestination !== AuthRoutes.app.loader) {
                    return internals.getRouteReplacement({
                        currentPath,
                        destination: RoutingTable.getRouteWithRedirect({
                            path: authDestination,
                            ...redirectContext
                        })
                    });
                }
            }
            else if (!isAuthenticated || !isVerified) {
                return internals.getRouteReplacement({
                    currentPath,
                    destination: RoutingTable.getRouteWithRedirect({
                        path: getAppAuthenticationRoute({ selectors, state: nextState }),
                        ...redirectContext
                    })
                });
            }

            if (internals.shouldBootstrapMainApp({
                state: nextState,
                selectors,
                location
            })) {
                const bootstrapSchoolId = internals.resolveBootstrapSchoolId({
                    state: nextState,
                    selectors,
                    location
                });

                redux.dispatch(actions.auth.bootstrapMainApp({
                    schoolId: bootstrapSchoolId,
                    redirect: redirectContext.redirect,
                    target: redirectContext.target
                }));

                if (!isAppLoaderRoute) {
                    return internals.getRouteReplacement({
                        currentPath,
                        destination: RoutingTable.getAppLoaderRoute({
                            ...redirectContext
                        })
                    });
                }

                return undefined;
            }

            if (isExact && isAppRoot) {
                return internals.getRouteReplacement({
                    currentPath,
                    destination: AuthRoutes.app.welcome
                });
            }
        }
        catch (err) {

            console.error(err);

            const destination = isAppLoaderRoute
                ? RoutingTable.getRouteWithRedirect({
                    path: AuthRoutes.home,
                    ...redirectContext
                })
                : RoutingTable.getAppLoaderRoute({
                    ...redirectContext
                });

            return internals.getRouteReplacement({
                currentPath,
                destination
            });
        }
    },
    onUnmount: () => handlers.communication.stopGlobalListening(),
    childRoutes: withRoutePermissions(withInterestCatalog(auditRoutes([
        {
            path: '/loader',
            component: AppLoader
        },
        {
            path: '/not-found',
            component: NotFound
        },
        {
            path: '/verify-phone',
            component: VerifyPhonePage,
            fallback: FallbackSpinner,
            onWillMount: internals.guardMfaPhoneVerification
        },
        {
            path: '/welcome',
            component: WelcomePage,
            fallback: FallbackSpinner
        },
        {
            path: '/profile/edit',
            component: ProfileEditPage,
            exact: true,
            props: {
                // See AnalyticsUtils.onRouteUpdate in 'src/utils/analytics.js' for how this ga prop is handled
                ga: ['navigation', 'edit profile', 'my profile: viewed edit profile']
            }
        },
        {
            path: '/profile/edit/interests',
            exact: true,
            component: InterestsEditPage,
            props: {
                // See AnalyticsUtils.onRouteUpdate in 'src/utils/analytics.js' for how this ga prop is handled
                ga: ['navigation', 'edit interests', 'my interests: viewed edit interests']
            }
        },
        {
            path: '/profile/change-password',
            exact: true,
            component: ChangePasswordPage,
            props: {
                // See AnalyticsUtils.onRouteUpdate in 'src/utils/analytics.js' for how this ga prop is handled
                ga: ['navigation', 'change password', 'viewed change-password']
            }
        },
        {
            path: '/profile/:userId',
            component: ProfileDetailPage,
            fallback: DelayedFallbackSpinner,
            dependencyTimeoutMs: 30000,
            dependencyPropTypes: profileDependencyPropTypes,
            resolveDependencies: {
                userDetails: internals.resolveProfileDetails,
                targetUserRole: internals.resolveProfileRoleDependencies,
                rolesInteractions: internals.resolveProfileRoleDependencies
            },
            onWillMount: ({ location }) => {

                const searchParams = new URLSearchParams(location.search);
                const fromDiscover = searchParams.get('fromDiscover') === 'true';
                const fromGroup = searchParams.get('fromGroup') === 'true';
                const groupId = searchParams.get('groupId');
                const index = searchParams.get('index');

                if (fromDiscover && index !== null) {
                    const parsedIndex = parseInt(index);
                    if (!isNaN(parsedIndex)) {
                        redux.dispatch(actions.app.setDiscoverNavigation({
                            currentResultIndex: parsedIndex,
                            fromDiscover: true
                        }));
                        redux.dispatch(actions.app.clearGroupNavigation());
                    }
                }
                else if (fromGroup && groupId && index !== null) {
                    const parsedIndex = parseInt(index);
                    const parsedGroupId = parseInt(groupId);
                    if (!isNaN(parsedIndex) && !isNaN(parsedGroupId)) {
                        redux.dispatch(actions.app.setGroupNavigation({
                            currentResultIndex: parsedIndex,
                            fromGroup: true,
                            groupId: parsedGroupId
                        }));
                        redux.dispatch(actions.app.clearDiscoverNavigation());
                    }
                }
                else if (!fromDiscover && !fromGroup) {
                    // Clear navigation state if not coming from discover or group
                    redux.dispatch(actions.app.clearDiscoverNavigation());
                    redux.dispatch(actions.app.clearGroupNavigation());
                }
            },
            props: {
                ga: ({ match: { params } }) => {

                    // If the user id is you
                    if (parseInt(params.userId) === getUserId()) {
                        return {
                            templateArgs: ['view profile', 'my profile: viewed my profile']
                        };
                    }

                    return {
                        templateArgs: ['viewed peer profile', 'peer profile: viewed peer profile']
                    };
                }
            }
        },
        {
            path: '/discover',
            exact: true,
            component: DiscoverPage,
            fallback: FallbackSpinner,
            resolveDependencies: {
                $always: internals.resolveDiscover
            },
            onWillMount: () => {
                // At this point in the program, we know we're going to this page but
                // history has _not yet changed_ its pathname.

                // We can take advantage of this by reading the historyPathname in redux

                const state = redux.getState();
                const discoverNavigation = selectors.getDiscoverNavigation(state);
                const isFromProfile = state.app.historyPreviousPathname.startsWith('/app/profile/');
                const shouldPreserveNavigation = discoverNavigation?.fromDiscover &&
                    (isFromProfile || typeof discoverNavigation.currentResultIndex === 'number');

                if (!shouldPreserveNavigation) {
                    redux.dispatch(actions.app.clearDiscoverNavigation());
                }

                // If coming from a profile page, don't re-run the default search
                // const previousPageIsProfile = state.app.historyPreviousPathname.startsWith('/app/profile/');
                const sortType = selectors.getDiscoverSortType(state);
                const hasPassionInterests = selectors.currentUserHasPassionInterests(state);

                const discoverSearchFilter = selectors.getDiscoverSearchFilter(state);

                redux.dispatch(actions.app.setRoleGroupFilter({
                    discover: discoverSearchFilter?.contextFilter || 'all'
                }));

                if (sortType === USERS_SORT_TYPE.PASSION && !hasPassionInterests) {
                    redux.dispatch(actions.app.setDiscoverSortType({ type: USERS_SORT_TYPE.BEST_MATCH }));
                }

            }
        },
        {
            path: '/nerpy',
            component: NerpyPage,
            exact: true,
            fallback: FallbackSpinner
        },
        {
            path: '/messaging',
            component: DirectMessagesPage,
            exact: true,
            fallback: FallbackSpinner,
            props: {
                ga: ['viewed connections messages', 'connections: loaded connections messages']
            },
            resolveDependencies: {
                $always: internals.resolveDirectMessagesList
            },
            onWillMount: () => {

                // Reset DMs filters when entering to ensure users don't think there's a bug
                redux.dispatch(actions.app.setRoleGroupFilter({ dms: 'all' }));
                redux.dispatch(actions.app.setDMsFilter({ filter: '' }));
            }
        },
        {
            path: '/messaging/:dmSid',
            exact: true,
            component: DirectMessageDetailPage,
            fallback: FallbackSpinner,
            mountWhileResolving: true,
            dependencyTimeoutMs: 30000,
            props: {
                ga: ['viewed peer chat', 'peer: viewed peer chat']
            },
            resolveDependencies: {
                currentUser: internals.resolveDirectMessageChat,
                header: internals.resolveDirectMessageChat,
                rolePermissions: internals.resolveDirectMessageChat,
                rolesInteractions: internals.resolveDirectMessageChat,
                userDetails: internals.resolveDirectMessageChat,
                users: internals.resolveDirectMessageChat
            },
            onWillMount: async ({ match: { params } }) => {

                const id = params.dmSid;

                const currentUserId = selectors.getCurrentUserId(redux.getState());

                // Formatted e.g. 'u:207' for user with id 207.
                // We want to convert this into a channelSid then redirect :)
                // Links with u:userId are found on the DirectMessagesPage
                // and User Details page for the Chat button
                if (id.slice(0, 2) === 'u:') {
                    if (!internals.isValidEntityId(id.slice(2))) {
                        return internals.replaceRouteWithToast({ destination: '/app/messaging', message: 'This conversation link is invalid.' });
                    }

                    // Redirect path
                    const { result: channelSid, error } = await communication.getDMChannelByUsers(
                        currentUserId,
                        id.slice(2)
                    );

                    if (internals.isValidChannelSid(channelSid) && !error) {
                        return { replace: `/app/messaging/${channelSid}` };
                    }

                    const data = await redux.dispatch(actions.communication.createTwilioConversation({
                        user1Id: currentUserId,
                        user2Id: id.slice(2)
                    }));

                    const sid = data?.payload?.result?.sid;

                    if (!internals.isValidChannelSid(sid)) {
                        return internals.replaceRouteWithToast({
                            destination: '/app/messaging',
                            message: 'We could not open this conversation. Please try again.'
                        });
                    }

                    return { replace: `/app/messaging/${sid}` };
                }

            }
        },
        {
            path: '/classes',
            exact: true,
            component: MyGroupsPage,
            fallback: FallbackSpinner,
            resolveDependencies: {
                $always: internals.resolveClassesList
            }
        },
        {
            path: '/classes/create',
            exact: true,
            component: EditGroupPage,
            requiredPermissions: ['canCreateGroups'],
            props: { isCreate: true }
        },
        {
            path: '/classes/:id',
            exact: true,
            component: ClassDetailPage,
            fallback: DelayedFallbackSpinner,
            mountWhileResolving: true,
            onWillMount: internals.guardClassDetails,
            resolveDependencies: {
                classDetails: internals.resolveClassDetails
            }
        },
        {
            path: '/classes/:id/edit',
            exact: true,
            component: EditGroupPage,
            requiredPermissions: ['canEditGroups']
        },
        {
            path: '/classes/chat/:sid',
            component: ClassChatFrame,
            exact: true,
            fallback: FallbackSpinner,
            mountWhileResolving: true,
            dependencyTimeoutMs: 30000,
            props: {
                ga: ['viewed class messages', 'class: viewed class messages']
            },
            onWillMount: internals.guardClassChat,
            resolveDependencies: internals.resolveClassChat,
            onUnmount: async ({ match: { params } }) => {

                const { sid } = params;

                const state = redux.getState();
                const id = selectors.getClassIdBySid(state, sid);

                const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};
                const classHasBeenFetched = id && selectors.getGroupHasBeenFetched(state, id);

                // Cleanup, remove user from twilio group
                if (classHasBeenFetched && !selectors.currentUserIsInClass(state, id) && rolePermissions.canSeeChatMessagesNoJoin) {
                    const currentUserId = selectors.getCurrentUserId(redux.getState());
                    await redux.dispatch(actions.chats.removeTwilioUserFromChat({
                        userId: currentUserId,
                        channelSid: sid
                    }));
                }
            }
        },
        {
            path: '/classes/announcement/chat/:id',
            component: AnnouncementClassChatFrame,
            exact: true,
            fallback: FallbackSpinner,
            mountWhileResolving: true,
            dependencyTimeoutMs: 30000,
            props: {
                ga: ['viewed announcement class messages', 'class: viewed announcement class messages']
            },
            resolveDependencies: {
                announcementConversation: internals.resolveAnnouncementChat,
                classId: internals.resolveAnnouncementChat,
                currentUser: internals.resolveAnnouncementChat,
                group: internals.resolveAnnouncementChat,
                rolePermissions: internals.resolveAnnouncementChat,
                rolesInteractions: internals.resolveAnnouncementChat
            }
        },
        {
            path: '/admin',
            component: AdminContainer,
            deniedRoleGroupIds: [USER_ROLE_GROUPS_IDS.STUDENT],
            childRoutes: [
                {
                    path: '/batch-notify-users',
                    component: BatchNotify,
                    requiredPermissions: ['canBatchNotify']
                },
                {
                    path: '/gamification',
                    component: GamificationAdminPage,
                    requiredPermissions: ['canAdministerGamification']
                },
                {
                    path: '/welcome-page',
                    component: WelcomePageManagement,
                    requiredPermissions: ['canManageWelcomePage']
                },
                {
                    path: '/preapprove-users',
                    component: PreapprovedEmailUpload,
                    requiredPermissions: ['canPreapprove']
                },
                {
                    path: '/dashboard',
                    component: AdminDashboard,
                    requiredPermissions: ['canViewDashboard'],
                    fallback: FallbackSpinner,
                    dependencyTimeoutMs: 30000,
                    dependencyPropTypes: dashboardDependencyPropTypes,
                    resolveDependencies: {
                        currentUser: internals.resolveAdminDashboard,
                        schools: internals.resolveAdminDashboard
                    }
                },
                {
                    path: '/offers',
                    exact: true,
                    component: AdminOffersListPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/offers/create',
                    exact: true,
                    component: AdminOfferCreateMethodPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/offers/create/manual',
                    exact: true,
                    component: AdminOfferFormPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/offers/create/upload',
                    exact: true,
                    component: AdminOfferUploadPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/offers/:id/edit/upload',
                    exact: true,
                    component: AdminOfferUploadPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/offers/:id/edit',
                    exact: true,
                    component: AdminOfferFormPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources',
                    exact: true,
                    component: AdminResourcesListPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources/create',
                    exact: true,
                    component: AdminResourceCreateMethodPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources/create/manual',
                    exact: true,
                    component: AdminResourceFormPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources/create/upload',
                    exact: true,
                    component: AdminResourceUploadPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources/:id/edit/upload',
                    exact: true,
                    component: AdminResourceUploadPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                },
                {
                    path: '/resources/:id/edit',
                    exact: true,
                    component: AdminResourceFormPage,
                    onWillMount: () => {

                        const state = redux.getState();
                        const currentUser = selectors.getCurrentUser(state)?.user() || null;
                        const email = currentUser?.email || '';

                        if (!email.endsWith('nearpeer.net') && !email.endsWith('nearpeer.com')) {
                            return { replace: '/app/not-found' };
                        }
                    }
                }
                // ...teamConsoleEnabled ? [{
                //     path: '/team-console',
                //     component: TeamConsole,
                //     requiredPermissions: TeamConsoleDependencies.requiredPermissions,
                //     requiredPermissionMode: 'any',
                //     fallback: FallbackSpinner,
                //     dependencyTimeoutMs: 30000,
                //     dependencyPropTypes: teamConsoleDependencyPropTypes,
                //     resolveDependencies: {
                //         currentUser: internals.resolveTeamConsole,
                //         rolePermissions: internals.resolveTeamConsole,
                //         schools: internals.resolveTeamConsole
                //     }
                // }] : []
            ]
        },
        {
            path: '/surveys',
            component: SurveysContainer,
            fallback: FallbackSpinner,
            resolveDependencies: {
                $always: internals.resolveSurveys
            },
            childRoutes: [
                {
                    path: '/create',
                    component: SurveysCreatePage,
                    requiredPermissions: ['canCreateSurveys'],
                    fallback: FallbackSpinner,
                    resolveDependencies: {
                        $always: internals.resolveSurveyCreate
                    }
                },
                {
                    path: '/:id/edit',
                    component: SurveysEditPage,
                    requiredPermissions: ['canCreateSurveys'],
                    fallback: FallbackSpinner,
                    resolveDependencies: {
                        $always: internals.resolveSurveyEdit
                    }
                },
                {
                    path: '/:id/report',
                    component: SurveyReportPage,
                    requiredPermissions: ['canSeeSurveysReports']
                },
                {
                    path: '/:id/start',
                    component: SurveySolvePage,
                    fallback: FallbackSpinner,
                    resolveDependencies: {
                        $always: internals.resolveSurveyStart
                    }
                },
                {
                    path: '/my-surveys',
                    component: MySurveysPage
                },
                {
                    path: '/management',
                    component: SurveysManagement,
                    requiredPermissions: ['canCreateSurveys']
                }
            ]
        },
        {
            path: '/manage-communities',
            exact: true,
            component: CommunityManagementPage
        },
        {
            path: '/offers',
            exact: true,
            component: OffersPage,
            onWillMount: async () => {

                await redux.dispatch(actions.dataFetching.fetchOffers());

                const state = redux.getState();
                const currentSchool = selectors.getCurrentSchool(state);

                if (!currentSchool?.offerListEnabled) {
                    const offerIds = selectors.getOffers(state);
                    if (offerIds && offerIds.length > 0) {
                        return { replace: `/app/offers/${offerIds[0].id}?fromList=true` };
                    }
                }
            }
        },
        {
            path: '/offers/:id',
            exact: true,
            component: OfferDetailPage,
            onWillMount: async ({ match }) => {

                if (!internals.isValidEntityId(match.params.id)) {
                    return internals.replaceRouteWithToast({ destination: '/app/offers', message: 'This link is invalid.' });
                }

                const id = Number(match.params.id);
                redux.dispatch(actions.dataFetching.fetchOffer({ id }));
                api.offers.recordImpression(id)
                    .then(() => redux.dispatch(actions.dataFetching.fetchOffers()))
                    .catch(() => null);
            }
        },
        {
            path: '/resources',
            exact: true,
            component: ResourcesPage,
            onWillMount: () => {

                redux.dispatch(actions.dataFetching.fetchResources());
            }
        },
        {
            path: '/resources/:id',
            exact: true,
            component: ResourceDetailPage,
            onWillMount: async ({ match }) => {

                if (!internals.isValidEntityId(match.params.id)) {
                    return internals.replaceRouteWithToast({ destination: '/app/resources', message: 'This link is invalid.' });
                }

                const id = Number(match.params.id);
                redux.dispatch(actions.dataFetching.fetchResource({ id }));
                api.resources.recordImpression(id)
                    .then(() => redux.dispatch(actions.dataFetching.fetchResources()))
                    .catch(() => null);
            }
        },
        {
            path: '/bug-report',
            component: BugReportPage
        },
        {
            path: '/community-invite-friends',
            exact: true,
            component: InviteFriendsPage,
            fallback: FallbackSpinner
        },
        {
            path: '/invite-friends',
            exact: true,
            component: InviteFriendsPage,
            fallback: FallbackSpinner
        },
        {
            path: '/preferences',
            component: UserPreferencesPage,
            fallback: FallbackSpinner,
            resolveDependencies: {
                $always: internals.resolvePreferences
            }
        },
        {
            redirect: {
                // Fallback redirect
                to: '/app/welcome'
            }
        }
    ]), context, FallbackSpinner), {
        getCurrentRole: () => selectors.getCurrentUserRole(redux.getState()),
        getRolePermissions: () => selectors.getCurrentUserRolePermissions(redux.getState()) || {}
    })
});

internals.sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

internals.isValidChannelSid = (sid) => typeof sid === 'string' && /^CH[a-zA-Z0-9]+$/.test(sid);

internals.isValidEntityId = (id) => /^[1-9]\d*$/.test(String(id)) && Number.isSafeInteger(Number(id));


internals.waitForRouteDependency = async ({ label, check, timeoutMs = 25000, intervalMs = 100 }) => {

    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
        const result = check();

        if (result) {
            return result;
        }

        await internals.sleep(intervalMs);
    }

    const err = new Error(`Timed out waiting for route dependency: ${label}`);
    err.code = 'ROUTE_DEPENDENCY_POSTCONDITION_TIMEOUT';
    err.label = label;

    throw err;
};

internals.replaceRouteWithToast = ({ destination, message }) => {

    redux.dispatch(actions.alerts.notification.push({ message }));

    return { replace: destination };
};

internals.replaceClassChatRoute = (params) => internals.replaceRouteWithToast(params);

internals.redirectFromClassChat = (message) => {

    return internals.replaceClassChatRoute({
        destination: AuthRoutes.app.welcome,
        message
    });
};

internals.guardMfaPhoneVerification = async () => {

    const state = redux.getState();

    return RoutingTable.guardProfilePhoneMfaRoute({
        isAuthenticated: selectors.getIsAuthenticated(state),
        loginContext: selectors.getLoginContext(state) || {}
    });
};

internals.guardClassChat = async ({ match: { params } }) => {

    const { sid } = params;

    if (!internals.isValidChannelSid(sid)) {
        return internals.redirectFromClassChat('This group is no longer available.');
    }

    let state = redux.getState();

    if (!selectors.getClassesHaveBeenFetched(state)) {
        await redux.dispatch(actions.dataFetching.fetchClasses());
        state = redux.getState();
    }

    if (!selectors.getClassesHaveBeenFetched(state)) {
        throw new Error(`Failed to load classes while guarding class chat ${sid}`);
    }

    const id = selectors.getClassIdBySid(state, sid);
    const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};

    if (!id) {
        return internals.redirectFromClassChat('This group is not available in your community.');
    }

    if (!selectors.getGroupHasBeenFetched(state, id)) {
        const fetchClassResult = await redux.dispatch(actions.dataFetching.fetchClass({ id }));

        if (!fetchClassResult) {
            return internals.redirectFromClassChat('We could not open this group. Please try again.');
        }

        state = redux.getState();
    }

    const group = selectors.getGroup_forAnnouncementChat(state, id);
    const userIsInClass = selectors.currentUserIsInClass(state, id);

    if (!canViewGroup({ group, rolePermissions, userIsInGroup: userIsInClass })) {
        return internals.redirectFromClassChat('You do not have access to this group.');
    }

    const replacementSid = selectors.getClassSidById(state, id);

    if (replacementSid && replacementSid !== sid) {
        return internals.replaceClassChatRoute({
            destination: `/app/classes/chat/${replacementSid}`,
            message: 'Group chat recovered.'
        });
    }

    if (!selectors.getGroupHasBeenFetched(state, id)) {
        return internals.redirectFromClassChat('We could not open this group. Please try again.');
    }
};

internals.guardClassDetails = async ({ match: { params } }) => {

    const { id } = params;

    if (!internals.isValidEntityId(id)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This link is invalid.' });
    }

    let state = redux.getState();

    if (!selectors.getClassesHaveBeenFetched(state)) {
        await redux.dispatch(actions.dataFetching.fetchClasses());
        state = redux.getState();
    }

    if (!selectors.getClassesHaveBeenFetched(state)) {
        throw new Error(`Failed to load classes while guarding class details ${id}`);
    }

    if (!selectors.getGroup_forAnnouncementChat(state, id)) {
        return internals.replaceRouteWithToast({
            destination: AuthRoutes.app.welcome,
            message: 'This group is not available in your community.'
        });
    }
};

internals.resolveProfileReferenceData = async () => {

    let state = redux.getState();

    if (!selectors.getCurrentUser(state)?.user() || !selectors.getCurrentSchoolId(state)) {
        await redux.dispatch(actions.dataFetching.fetchCurrentUser());
        state = redux.getState();
    }

    await ensureInterestCatalog(context);
};

internals.resolveProfileDetails = async ({ routerProps }) => {

    const { userId } = routerProps.match.params;

    if (!internals.isValidEntityId(userId)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This profile link is invalid.' });
    }

    const getUserDetails = () => selectors.getUser_forDetails(redux.getState(), userId);
    const isWrongSchool = () => {

        const state = redux.getState();
        const profile = state.dataFetching?.entities?.users?.[userId];

        return profile?.schoolId != null && Number(profile.schoolId) !== Number(selectors.getCurrentSchoolId(state));
    };

    if (isWrongSchool()) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This profile is not available in your community.' });
    }

    await internals.resolveProfileReferenceData();

    if (getUserDetails()) {
        return;
    }

    const currentUserId = selectors.getCurrentUserId(redux.getState());
    const isCurrentUserProfile = Number(userId) === Number(currentUserId);

    if (isCurrentUserProfile) {
        await redux.dispatch(actions.dataFetching.fetchCurrentUser());
        return;
    }

    if (!getUserDetails()) {
        await redux.dispatch(actions.dataFetching.fetchUser({ id: userId }));
    }

    if (!getUserDetails() || isWrongSchool()) {
        return { replace: AuthRoutes.app.welcome };
    }

    await internals.waitForRouteDependency({
        label: `profile userDetails for ${userId}`,
        check: () => {
            const userDetails = getUserDetails();
            const state = redux.getState();

            return userDetails && !selectors.getIsLoadingInterests(state) ? userDetails : null;
        }
    });
};


internals.resolveProfileRoleDependencies = async ({ routerProps }) => {

    const result = await internals.resolveProfileDetails({ routerProps });
    if (result?.replace) {
        return result;
    }

    const { userId } = routerProps.match.params;

    const checkProfileRoleDependencies = () => {

        const state = redux.getState();
        const userDetails = selectors.getUser_forDetails(state, userId);
        const targetUserRole = selectors.getUserRole(state, userId);
        const rolesInteractions = selectors.getCurrentUserRoleInteractions(state) || [];
        const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};

        if (!userDetails) {
            return false;
        }

        if (userDetails.isMe) {
            return true;
        }

        if (!targetUserRole) {
            return false;
        }

        if (canBypassProfileRoleInteractions(rolePermissions)) {
            return true;
        }

        return rolesInteractions.some(({ id }) => id === targetUserRole.id);
    };

    if (checkProfileRoleDependencies()) {
        return;
    }

    await redux.dispatch(actions.dataFetching.fetchCurrentUser());

    const currentSchoolId = selectors.getCurrentSchoolId(redux.getState());
    if (currentSchoolId) {
        await redux.dispatch(actions.dataFetching.fetchAppEntities({
            entities: ['roles', 'roleGroups'],
            schoolId: currentSchoolId
        }));
    }

    await internals.waitForRouteDependency({
        label: `profile role dependencies for ${userId}`,
        check: checkProfileRoleDependencies
    });
};

internals.resolveClassDetails = async ({ routerProps }) => {

    const classId = routerProps.match.params.id;

    if (!internals.isValidEntityId(classId)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This link is invalid.' });
    }

    const state = redux.getState();
    const classHasBeenFetched = !!selectors.getGroupHasBeenFetched(state, classId);

    if (!classHasBeenFetched) {
        await redux.dispatch(actions.dataFetching.fetchClass({ id: classId }));
    }

    const nextState = redux.getState();
    const group = selectors.getGroup_forAnnouncementChat(nextState, classId);
    const userIsInClass = !!selectors.currentUserIsInClass(nextState, classId);
    const canViewWithoutJoining = !!selectors.getCurrentUserRolePermissions(nextState)?.canSeeChatMessagesNoJoin;

    if (!canViewGroup({
        group,
        rolePermissions: { canSeeChatMessagesNoJoin: canViewWithoutJoining },
        userIsInGroup: userIsInClass
    })) {
        return internals.replaceRouteWithToast({ destination: '/app/classes', message: 'This group is not available or you do not have access to it.' });
    }

    await internals.waitForRouteDependency({
        label: `class detail render props for ${classId}`,
        check: () => {
            const latestState = redux.getState();
            const classDetails = selectors.getGroup_forGroupDetail_withUserSearchResults(latestState, classId);

            return classDetails?.class && Array.isArray(classDetails.users);
        }
    });
};


internals.resolveDirectMessageChat = async (params) => {

    try {
        return await internals.resolveDirectMessageChatDetails(params);
    }
    catch (error) {

        if (error.code !== 'ROUTE_DEPENDENCY_POSTCONDITION_TIMEOUT') {
            throw error;
        }

        return internals.replaceRouteWithToast({ destination: '/app/messaging', message: 'This conversation is no longer available.' });
    }
};

internals.resolveDirectMessageChatDetails = async ({ routerProps }) => {

    const sid = routerProps.match.params.dmSid || routerProps.match.params.sid;

    if (!internals.isValidChannelSid(sid)) {
        return internals.replaceRouteWithToast({ destination: '/app/messaging', message: 'This conversation is no longer available.' });
    }

    const users = await internals.waitForRouteDependency({
        label: `dm users for ${sid}`,
        check: () => {
            const state = redux.getState();
            const usersForChat = selectors.getDmUsers_forChat(state, sid) || [];

            return usersForChat.length ? usersForChat : null;
        }
    });

    const currentUserId = selectors.getCurrentUserId(redux.getState());
    const otherUser = users.find(({ id }) => id !== currentUserId);
    const otherUserExtension = otherUser?.id
        ? redux.getState()?.dataFetching?.entities?.userExtensions?.[otherUser.id]
        : null;

    if (otherUser?.id && (!selectors.getUser_forDetails(redux.getState(), otherUser.id) || !otherUserExtension)) {
        await redux.dispatch(actions.dataFetching.fetchUser({ id: otherUser.id }));
    }

    await internals.waitForRouteDependency({
        label: `dm render props for ${sid}`,
        check: () => {
            const state = redux.getState();

            return selectors.getCurrentUser(state)?.user()
                && selectors.getCurrentUserRolePermissions(state)
                && selectors.getCurrentUserRoleInteractions(state)
                && selectors.getName_forChat(state, sid)
                && selectors.getUserDetails_forChat(state, sid)
                && (selectors.getDmUsers_forChat(state, sid) || []).length;
        }
    });
};

internals.resolveClassChat = async ({ routerProps }) => {

    const { sid } = routerProps.match.params;
    const id = selectors.getClassIdBySid(redux.getState(), sid);

    if (!id) {
        return internals.redirectFromClassChat('This group is not available in your community.');
    }

    const state = redux.getState();
    const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};
    const group = selectors.getGroup_forAnnouncementChat(state, id);
    const userIsInClass = selectors.currentUserIsInClass(state, id);

    if (!canViewGroup({ group, rolePermissions, userIsInGroup: userIsInClass })) {
        return internals.redirectFromClassChat('You do not have access to this group.');
    }

    if (!userIsInClass && rolePermissions.canSeeChatMessagesNoJoin) {
        const currentUserId = selectors.getCurrentUserId(redux.getState());
        const addUserResult = await redux.dispatch(actions.chats.addTwilioUserToChat({
            userId: currentUserId,
            channelSid: sid
        }));

        if (!addUserResult) {
            return internals.redirectFromClassChat('We could not open this group. Please try again.');
        }

        const replacementSid = addUserResult.payload?.result?.sid;

        if (replacementSid && replacementSid !== sid) {
            await redux.dispatch(actions.dataFetching.fetchClasses());

            return internals.replaceClassChatRoute({
                destination: `/app/classes/chat/${replacementSid}`,
                message: 'Group chat recovered.'
            });
        }
    }

    await internals.waitForRouteDependency({
        label: `class chat render props for ${sid}`,
        check: () => {
            const nextState = redux.getState();

            return selectors.getCurrentUser(nextState)?.user()
                && selectors.getCurrentUserRolePermissions(nextState)
                && selectors.getCurrentUserRoleInteractions(nextState)
                && selectors.getGroupHasBeenFetched(nextState, id);
        }
    });
};

internals.resolveAnnouncementChat = async ({ routerProps }) => {

    const { id } = routerProps.match.params;

    if (!internals.isValidEntityId(id)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This link is invalid.' });
    }

    const state = redux.getState();
    const rolePermissions = selectors.getCurrentUserRolePermissions(state) || {};
    const initialGroup = selectors.getGroup_forAnnouncementChat(state, id);
    const userIsInClass = selectors.currentUserIsInClass(state, id);

    if (!id || (initialGroup && !canViewGroup({
        group: initialGroup,
        rolePermissions,
        userIsInGroup: userIsInClass
    }))) {
        return internals.replaceRouteWithToast({ destination: '/app/not-found', message: 'This announcement is not available or you do not have access to it.' });
    }

    await Promise.all([
        redux.dispatch(actions.dataFetching.fetchClass({ id })),
        redux.dispatch(actions.dataFetching.fetchAnnouncementConversations())
    ]);

    const nextState = redux.getState();
    const group = selectors.getGroup_forAnnouncementChat(nextState, id);

    if (!group?.id || !group.isAnnouncement || !canViewGroup({
        group,
        rolePermissions,
        userIsInGroup: selectors.currentUserIsInClass(nextState, id)
    })) {
        return internals.replaceRouteWithToast({ destination: '/app/not-found', message: 'This announcement is not available or you do not have access to it.' });
    }

    await internals.waitForRouteDependency({
        label: `announcement chat render props for ${id}`,
        check: () => {
            const nextState = redux.getState();

            return selectors.getCurrentUser(nextState)?.user()
                && selectors.getCurrentUserRolePermissions(nextState)
                && selectors.getCurrentUserRoleInteractions(nextState)
                && selectors.getGroup_forAnnouncementChat(nextState, id)?.id
                && selectors.getAnnouncementConversationByClassId(nextState, id);
        }
    });
};

internals.resolveDirectMessagesList = async () => {

    const state = redux.getState();

    await Promise.all([
        redux.dispatch(actions.dataFetching.fetchConversationStarters({
            schoolId: selectors.getCurrentSchoolId(state)
        })),
        redux.dispatch(actions.dataFetching.fetchChannels())
    ]);
};

internals.resolveClassesList = async () => {

    // `fetchChannels`/`fetchAllChannelLastMessages` are for Twilio-backed DM/group chats.
    // Announcement conversations are DB-backed and must be fetched separately.
    await Promise.all([
        redux.dispatch(actions.dataFetching.fetchClasses()),
        redux.dispatch(actions.dataFetching.fetchAnnouncementConversations()),
        redux.dispatch(actions.dataFetching.fetchChannels())
    ]);
};

internals.resolveAdminDashboard = async () => {

    const getDashboardDependencies = () => {

        const state = redux.getState();
        const currentUser = selectors.getCurrentUser(state)?.user();
        const schools = selectors.getSchools(state) || [];

        return currentUser && schools.length;
    };

    if (getDashboardDependencies()) {
        return;
    }

    await redux.dispatch(actions.dataFetching.fetchCurrentUser());

    if (!selectors.getSchools(redux.getState())?.length) {
        await redux.dispatch(actions.dataFetching.fetchAppEntities({
            entities: ['schools']
        }));
    }

    await internals.waitForRouteDependency({
        label: 'admin dashboard currentUser and schools',
        check: getDashboardDependencies
    });
};

internals.resolveTeamConsole = async () => {

    const getTeamConsoleDependencies = () => {

        const state = redux.getState();
        const currentUser = selectors.getCurrentUser(state)?.user();
        const rolePermissions = selectors.getCurrentUserRolePermissions(state);
        const schools = selectors.getSchools(state) || [];

        return currentUser && rolePermissions && schools.length;
    };

    if (getTeamConsoleDependencies()) {
        return;
    }

    await redux.dispatch(actions.dataFetching.fetchCurrentUser());

    if (!selectors.getSchools(redux.getState())?.length) {
        await redux.dispatch(actions.dataFetching.fetchAppEntities({
            entities: ['schools']
        }));
    }

    await internals.waitForRouteDependency({
        label: 'team console currentUser, rolePermissions, and schools',
        check: getTeamConsoleDependencies
    });
};


internals.resolveSurveys = async () => {

    await redux.dispatch(actions.dataFetching.fetchSurveys());
};

internals.resolvePreferences = async () => {

    await redux.dispatch(actions.dataFetching.fetchPreferences());
};


internals.resolveDiscover = async () => {

    await internals.resolveProfileReferenceData();

    const state = redux.getState();
    const sortType = selectors.getDiscoverSortType(state);
    const hasPassionInterests = selectors.currentUserHasPassionInterests(state);
    const discoverSearchFilter = selectors.getDiscoverSearchFilter(state);
    const { discoverMatching: debug } = selectors.getDebug(state);

    const filter = discoverSearchFilter && !(Object.keys(discoverSearchFilter).length === 0)
        ? discoverSearchFilter
        : {
            allByMatches: true,
            sortType: sortType === USERS_SORT_TYPE.PASSION && !hasPassionInterests ? USERS_SORT_TYPE.BEST_MATCH : sortType,
            debugMatching: !!debug
        };

    const passedRefreshThreshold = Date.now() - state.app.refreshSearchTimestamps.discover > SEARCH_REFRESH_TIME_LIMIT;

    if (state.app.shouldRefreshSearch.discover || passedRefreshThreshold) {
        let searchSucceeded = false;

        try {
            const result = await redux.dispatch(actions.dataFetching.fetchDiscoverSearchResults.firstPage(filter));

            searchSucceeded = !!result;
        }
        catch (err) {
            console.warn('[routes] discover bootstrap search failed; rendering route shell without blocking', {
                filter,
                message: err?.message
            });
        }

        redux.dispatch(actions.app.setShouldRefreshSearch({ discover: !searchSucceeded }));
    }
};


internals.resolveSurveyCreate = async ({ routerProps }) => {

    const searchParams = new URLSearchParams(routerProps.location.search);
    const copyFromId = searchParams.get('copyFrom');

    if (copyFromId) {
        await redux.dispatch(actions.dataFetching.fetchSurvey({
            id: copyFromId,
            includeUserAnswers: false
        }));
    }
};

internals.resolveSurveyEdit = async ({ routerProps }) => {

    const surveyId = routerProps.match.params.id;

    if (!internals.isValidEntityId(surveyId)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This link is invalid.' });
    }

    await redux.dispatch(actions.dataFetching.fetchSurvey({
        id: surveyId,
        includeUserAnswers: false
    }));
};

internals.resolveSurveyStart = async ({ routerProps }) => {

    const surveyId = routerProps.match.params.id;

    if (!internals.isValidEntityId(surveyId)) {
        return internals.replaceRouteWithToast({ destination: AuthRoutes.app.welcome, message: 'This link is invalid.' });
    }

    const fetchResult = await redux.dispatch(actions.dataFetching.fetchSurvey({
        id: surveyId,
        includeUserAnswers: true
    }));

    if (!fetchResult) {
        // The fetch failure handler already reports the error and handles 404s.
        return { replace: AuthRoutes.app.welcome };
    }

    const userSurvey = selectors.getCurrentUserSurveys_byId(redux.getState(), surveyId);

    if (!userSurvey || !userSurvey.isActive) {
        return internals.replaceRouteWithToast({ destination: '/app/not-found', message: 'This survey is no longer available.' });
    }
};

if (process.env.NODE_ENV === 'test') {
    module.exports._internals = internals;
}
